import { useCallback } from 'react';

import { authApi } from '@/entities/auth';
import { useSessionStore, type Session } from '@/shared/api';

import { useTourStore } from './tour-store';

/** De sessie met dit moment als het einde van de rondleiding. */
function withTourCompletedAt(session: Session, at: string | null): Session {
    return { ...session, user: { ...session.user, tourCompletedAt: at } };
}

/**
 * Rondt de rondleiding af: meteen hier, en daarna op de server.
 *
 * Eerst lokaal, zodat hij niet terugkomt terwijl het verzoek nog onderweg is of
 * als er geen verbinding is. Dan `POST /auth/me/tour`, zodat hij op geen enkel
 * apparaat meer vanzelf komt. Lukt dat niet, dan is dat geen fout voor de
 * gebruiker: bij een volgende start geeft `GET /auth/me` weer `null` en mag de
 * rondleiding opnieuw - beter dan hem kwijt zijn.
 */
export function useCompleteTour(): () => void {
    const close = useTourStore((state) => state.close);

    return useCallback(() => {
        const session = useSessionStore.getState().session;

        close(session?.user.uuid ?? null);

        if (!session) {
            return;
        }

        const signIn = useSessionStore.getState().signIn;

        void signIn(withTourCompletedAt(session, new Date().toISOString()));

        void authApi
            .completeTour(session)
            .then((at) => {
                const current = useSessionStore.getState().session;

                if (current?.token === session.token) {
                    return signIn(withTourCompletedAt(current, at));
                }

                return undefined;
            })
            .catch(() => undefined);
    }, [close]);
}
